import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import { useBlogPosts, type BlogPost } from '@/hooks/useBlogPosts';
import PostEditor from './components/PostEditor';
import SaveNotice from './components/SaveNotice';

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function BlogAdmin() {
  useEffect(() => {
    document.title = 'Blog Admin | Federica Tedeschi';
    const metaRobots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (metaRobots) metaRobots.content = 'noindex, nofollow';
    // Set admin session flag so blog posts show edit links
    try {
      localStorage.setItem('blogAdminSession', '1');
    } catch {
      // localStorage not available
    }
    return () => {
      document.title = 'Federica Tedeschi | Energy-Safe Transformational Coach for Chronic Fatigue & Pain';
      if (metaRobots) metaRobots.content = 'index, follow';
      // Clear admin session when leaving admin
      try {
        localStorage.removeItem('blogAdminSession');
      } catch {
        // localStorage not available
      }
    };
  }, []);
  const { posts, getPostById, createPost, updatePost, deletePost, resetToDefaults, saveError } = useBlogPosts();
  const [saveNotice, setSaveNotice] = useState<string | null>(null);

  // Saving is asynchronous, so confirm it on screen instead of leaving the user
  // guessing whether the click actually worked.
  useEffect(() => {
    if (!saveNotice) return;
    const timer = window.setTimeout(() => setSaveNotice(null), 8000);
    return () => window.clearTimeout(timer);
  }, [saveNotice]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const editId = searchParams.get('edit');

  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';

  useEffect(() => {
    let authed = false;
    try {
      authed = localStorage.getItem('blogAdminAuthed') === '1';
    } catch {
      // localStorage not available
    }
    if (!authed) {
      navigate('/blog', { replace: true });
    }
  }, [navigate, pathPrefix]);

  useEffect(() => {
    if (editId && getPostById(editId)) {
      setSelectedId(editId);
    }
  }, [editId, getPostById]);

  const selectedPost = selectedId ? getPostById(selectedId) || null : null;

  const filteredPosts = useMemo(() => {
    let list = posts;
    if (filter === 'published') list = posts.filter((p) => p.status === 'published');
    else if (filter === 'draft') list = posts.filter((p) => p.status === 'draft');
    // Sort by the publish date the user set, newest first.
    return [...list].sort((a, b) => {
      const da = new Date(a.publishedAt || a.createdAt).getTime();
      const db = new Date(b.publishedAt || b.createdAt).getTime();
      return db - da;
    });
  }, [posts, filter]);

  const publishedCount = posts.filter((p) => p.status === 'published').length;
  const draftCount = posts.filter((p) => p.status === 'draft').length;

  const handleNew = useCallback(() => {
    setSelectedId(null);
    setSearchParams({});
  }, [setSearchParams]);

  const handleEdit = useCallback(
    (id: string) => {
      setSelectedId(id);
      setSearchParams({ edit: id });
    },
    [setSearchParams],
  );

  const handleBackToList = useCallback(() => {
    setSelectedId(null);
    setSearchParams({});
  }, [setSearchParams]);

  const handleSave = useCallback(
    (data: {
      title: string;
      slug: string;
      excerpt: string;
      coverImage: string;
      content: string;
      category: string;
      tags: string[];
      status: 'draft' | 'published';
      publishedAt: string;
    }) => {
      if (selectedPost) {
        const updated = updatePost(selectedPost.id, {
          ...data,
          publishedAt: data.publishedAt || selectedPost.publishedAt,
        });
        if (updated) {
          setSelectedId(updated.id);
          setSearchParams({ edit: updated.id });
        }
        setSaveNotice(
          data.status === 'published'
            ? `Published: “${data.title}” — dated ${formatDate(data.publishedAt)}. It now appears in your blog list.`
            : `Draft saved: “${data.title}”.`,
        );
      } else {
        const newPost = createPost({
          ...data,
          author: 'Federica Tedeschi',
          publishedAt: data.publishedAt || new Date().toISOString(),
        });
        setSelectedId(newPost.id);
        setSearchParams({ edit: newPost.id });
        setSaveNotice(
          data.status === 'published'
            ? `Published: “${newPost.title}” — dated ${formatDate(newPost.publishedAt)}. It now appears in your blog list.`
            : `Draft saved: “${newPost.title}”.`,
        );
      }
    },
    [selectedPost, updatePost, createPost, setSearchParams],
  );

  const handleDelete = useCallback(
    (id: string) => {
      if (selectedId === id) {
        setSelectedId(null);
        setSearchParams({});
      }
      deletePost(id);
      setDeleteConfirmId(null);
    },
    [selectedId, deletePost, setSearchParams],
  );

  const handlePreview = useCallback(
    (slug: string) => {
      window.open(`${window.location.origin}${pathPrefix}/blog/${slug}`, '_blank');
    },
    [pathPrefix],
  );

  const isEditorMode = selectedId !== null || searchParams.get('edit') === null && selectedId === null && searchParams.toString() === '';
  // If we're not actively editing a specific post and no edit param, we're in list mode.
  // But when selectedId is null and we clicked New Post, we want editor.
  // Let's use a simpler approach: if selectedId !== null, or if we're in "new" mode.
  // Actually, let me track mode more explicitly.

  // Actually, the simplest: when selectedId is set, show editor. When null AND we have posts to show, show list.
  // But when "New Post" is clicked, selectedId stays null but we want editor.
  // I'll add an explicit `isNew` flag.

  // Let me restructure: use a view state.
  const [view, setView] = useState<'list' | 'editor'>('list');

  useEffect(() => {
    if (editId) {
      setView('editor');
    }
  }, [editId]);

  const startNew = useCallback(() => {
    setSelectedId(null);
    setSearchParams({});
    setView('editor');
  }, [setSearchParams]);

  const startEdit = useCallback(
    (id: string) => {
      setSelectedId(id);
      setSearchParams({ edit: id });
      setView('editor');
    },
    [setSearchParams],
  );

  const goToList = useCallback(() => {
    setSelectedId(null);
    setSearchParams({});
    setView('list');
  }, [setSearchParams]);

  // Table view
  if (view === 'list') {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-[72px]">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
              <div>
                <a
                  href={`${pathPrefix}/blog`}
                  className="text-sm text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer mb-3"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-sm" />
                  </span>
                  Back to Blog
                </a>
                <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900">
                  Blog Admin
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  Manage your blog posts, create new articles, and preview before publishing.
                </p>
              </div>              <button
                onClick={startNew}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-primary-600 text-background-50 rounded-md hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap self-start md:self-auto"
                type="button"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-add-line text-sm" />
                </span>
                New Post
              </button>
            </div>

            <SaveNotice
              message={saveNotice}
              error={saveError}
              onDismiss={() => setSaveNotice(null)}
            />

            {/* Filters & Count */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div className="inline-flex items-center bg-background-100 rounded-md p-1">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1.5 text-sm font-medium rounded transition-colors cursor-pointer whitespace-nowrap ${
                    filter === 'all'
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                  type="button"
                >
                  All ({posts.length})
                </button>
                <button
                  onClick={() => setFilter('published')}
                  className={`px-3 py-1.5 text-sm font-medium rounded transition-colors cursor-pointer whitespace-nowrap ${
                    filter === 'published'
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                  type="button"
                >
                  Published ({publishedCount})
                </button>
                <button
                  onClick={() => setFilter('draft')}
                  className={`px-3 py-1.5 text-sm font-medium rounded transition-colors cursor-pointer whitespace-nowrap ${
                    filter === 'draft'
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                  type="button"
                >
                  Draft ({draftCount})
                </button>
              </div>
              <p className="text-sm text-foreground-500">
                Total: {filteredPosts.length} posts
              </p>
            </div>

            {/* Table */}
            {filteredPosts.length === 0 ? (
              <div className="bg-background-50 border border-background-200 rounded-lg p-10 text-center">
                <p className="text-sm text-foreground-500 mb-4">
                  No posts found. Click "New Post" to create one, or restore sample posts below.
                </p>
                <button
                  onClick={resetToDefaults}
                  className="px-4 py-2 text-sm font-medium bg-secondary-100 text-secondary-700 rounded-md hover:bg-secondary-200 transition-colors cursor-pointer"
                  type="button"
                >
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                    <i className="ri-refresh-line text-sm" />
                  </span>
                  Restore Sample Posts
                </button>
              </div>
            ) : (
              <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                {/* Table Header */}
                <div className="hidden md:grid grid-cols-[1fr_140px_120px_100px_120px] gap-4 px-5 py-3 bg-background-100 border-b border-background-200">
                  <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Post</span>
                  <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Author</span>
                  <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Date</span>
                  <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Status</span>
                  <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider text-right">Actions</span>
                </div>

                {/* Table Rows */}
                <div className="divide-y divide-background-100">
                  {filteredPosts.map((post) => (
                    <div
                      key={post.id}
                      className="flex flex-col md:grid md:grid-cols-[1fr_140px_120px_100px_120px] gap-4 px-5 py-4 hover:bg-background-100/50 transition-colors"
                    >
                      {/* Post */}
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-14 h-14 rounded-md overflow-hidden flex-shrink-0 bg-background-100 border border-background-200">
                          {post.coverImage ? (
                            <img
                              src={post.coverImage}
                              alt={post.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <span className="w-5 h-5 flex items-center justify-center">
                                <i className="ri-image-line text-sm text-foreground-300" />
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground-900 truncate">
                            {post.title || 'Untitled Post'}
                          </p>
                          <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">
                            {post.excerpt || 'No excerpt'}
                          </p>
                        </div>
                      </div>

                      {/* Author */}
                      <div className="flex items-center md:items-start">
                        <span className="md:hidden text-xs font-medium text-foreground-400 mr-2">Author:</span>
                        <span className="text-sm text-foreground-700">{post.author}</span>
                      </div>

                      {/* Date */}
                      <div className="flex items-center md:items-start">
                        <span className="md:hidden text-xs font-medium text-foreground-400 mr-2">Date:</span>
                        <span className="text-sm text-foreground-600">{formatDate(post.publishedAt || post.createdAt)}</span>
                      </div>

                      {/* Status */}
                      <div className="flex items-center md:items-start">
                        <span className="md:hidden text-xs font-medium text-foreground-400 mr-2">Status:</span>
                        <span
                          className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${
                            post.status === 'published'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-background-200 text-foreground-500'
                          }`}
                        >
                          {post.status === 'published' && (
                            <span className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-check-line text-xs" />
                            </span>
                          )}
                          {post.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center justify-start md:justify-end gap-1">
                        <span className="md:hidden text-xs font-medium text-foreground-400 mr-2">Actions:</span>
                        {post.status === 'published' && (
                          <button
                            onClick={() => handlePreview(post.slug)}
                            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                            title="Preview"
                            type="button"
                            aria-label="Preview post"
                          >
                            <span className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-eye-line text-sm" />
                            </span>
                          </button>
                        )}
                        <button
                          onClick={() => startEdit(post.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                          title="Edit"
                          type="button"
                          aria-label="Edit post"
                        >
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-pencil-line text-sm" />
                          </span>
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(post.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                          title="Delete"
                          type="button"
                          aria-label="Delete post"
                        >
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-delete-bin-line text-sm" />
                          </span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirmId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
            <div className="bg-background-50 rounded-lg shadow-lg max-w-sm w-full p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                Delete Post?
              </h3>
              <p className="text-sm text-foreground-600 mb-6">
                Are you sure you want to delete this post? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-md transition-colors cursor-pointer"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirmId)}
                  className="px-4 py-2 text-sm font-medium bg-red-500 text-background-50 rounded-md hover:bg-red-600 transition-colors cursor-pointer"
                  type="button"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Editor view
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="pt-[72px]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <button
                onClick={goToList}
                className="text-sm text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer mb-3"
                type="button"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm" />
                </span>
                Back to Admin
              </button>
              <h1 className="font-heading text-2xl font-semibold text-foreground-900">
                {selectedPost ? 'Edit Post' : 'New Post'}
              </h1>
              <p className="text-sm text-foreground-500 mt-0.5">
                {selectedPost
                  ? `Editing: ${selectedPost.title || 'Untitled'}`
                  : 'Create a new blog post'}
              </p>
            </div>
          </div>

          <SaveNotice
            message={saveNotice}
            error={saveError}
            onDismiss={() => setSaveNotice(null)}
          />

          <PostEditor
            post={selectedPost}
            onSave={handleSave}
            onPreview={handlePreview}
          />
        </div>
      </div>
    </div>
  );
}